import React, { useState } from "react";
import api from "../../services/api";

interface ZReportData {
  summary: {
    transaction_count: string;
    total_sales: string;
    cash_sales: string;
    credit_sales: string;
    debit_sales: string;
    void_count: string;
    tips_total?: string;
    tip_count?: string;
  };
}

interface DrawerCountProps {
  mode: "open" | "close";
  sessionId?: string | null;
  openingBalance?: number;
  cashSalesTotal?: number;
  cashRefundsTotal?: number;
  nextDrawerStartAmount?: number;
  onComplete: (total: number) => void;
  onCancel: () => void;
}

const DENOMINATIONS = [
  { label: "Pennies", value: 0.01, icon: "1c" },
  { label: "Nickels", value: 0.05, icon: "5c" },
  { label: "Dimes", value: 0.10, icon: "10c" },
  { label: "Quarters", value: 0.25, icon: "25c" },
  { label: "Half Dollars", value: 0.50, icon: "50c" },
  { label: "Dollar Coins", value: 1.00, icon: "$1" },
  { label: "$1 Bills", value: 1.00, icon: "$1" },
  { label: "$5 Bills", value: 5.00, icon: "$5" },
  { label: "$10 Bills", value: 10.00, icon: "$10" },
  { label: "$20 Bills", value: 20.00, icon: "$20" },
  { label: "$50 Bills", value: 50.00, icon: "$50" },
  { label: "$100 Bills", value: 100.00, icon: "$100" },
];

const DEFAULT_NEXT_START = 200; // Default amount to leave in drawer for next shift

export default function DrawerCount({
  mode, sessionId, openingBalance = 0, cashSalesTotal = 0,
  cashRefundsTotal = 0, nextDrawerStartAmount, onComplete, onCancel,
}: DrawerCountProps) {
  const [counts, setCounts] = useState<Record<number, string>>(
    Object.fromEntries(DENOMINATIONS.map((_, i) => [i, ""]))
  );
  const [step, setStep] = useState<"count" | "closeout" | "summary">("count");
  const [zReport, setZReport] = useState<ZReportData | null>(null);

  const updateCount = (index: number, value: string) => {
    setCounts({ ...counts, [index]: value });
  };

  const getSubtotal = (index: number): number => {
    const qty = parseInt(counts[index]) || 0;
    return qty * DENOMINATIONS[index].value;
  };

  const coinTotal = DENOMINATIONS.slice(0, 6).reduce((sum, _, i) => sum + getSubtotal(i), 0);
  const billTotal = DENOMINATIONS.slice(6).reduce((sum, _, i) => sum + getSubtotal(i + 6), 0);
  const grandTotal = Math.round((coinTotal + billTotal) * 100) / 100;

  // Closeout calculations
  const expectedInDrawer = Math.round((openingBalance + cashSalesTotal - cashRefundsTotal) * 100) / 100;
  const overShort = Math.round((grandTotal - expectedInDrawer) * 100) / 100;
  const leaveInDrawer = nextDrawerStartAmount ?? DEFAULT_NEXT_START;
  const pullForDeposit = Math.max(0, Math.round((grandTotal - leaveInDrawer) * 100) / 100);

  const handleSubmit = async () => {
    const total = grandTotal;

    const denominations: Record<string, number> = {};
    DENOMINATIONS.forEach((denom, i) => {
      const qty = parseInt(counts[i]) || 0;
      if (qty > 0) {
        denominations[denom.label] = qty;
      }
    });

    try {
      await api.post("/checklists/drawer-counts", {
        session_id: sessionId || null,
        count_type: mode === "open" ? "opening" : "closing",
        denominations,
        total,
      });
    } catch {
      // drawer count save is best-effort
    }

    if (mode === "close") {
      // Fetch Z-report for end-of-day summary
      try {
        const today = new Date().toISOString().split("T")[0];
        const res = await api.get(`/reports/z-report?date=${today}`);
        setZReport(res.data);
        setStep("summary");
        return; // Don't complete yet — show summary first
      } catch {
        // If Z-report fails, just complete
      }
    }

    onComplete(total);
  };

  const handleCountDone = () => {
    if (mode === "close") {
      setStep("closeout");
    } else {
      handleSubmit();
    }
  };

  // End-of-day summary with Z-report
  if (step === "summary" && zReport) {
    const z = zReport.summary;
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: "rgba(51,63,72,0.85)" }}>
        <div className="w-full max-w-md rounded-2xl overflow-hidden" style={{ background: "var(--rbw-bg-card)", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
          <div className="px-6 py-4" style={{ background: "var(--rbw-navy)" }}>
            <h2 className="text-lg font-bold text-white">End of Day Summary</h2>
            <p className="text-xs" style={{ color: "var(--rbw-gray)" }}>
              {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
            </p>
          </div>

          <div className="px-6 py-5 space-y-3">
            {/* Sales summary */}
            <div className="rounded-xl p-4" style={{ background: "var(--rbw-bg)" }}>
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm font-semibold" style={{ color: "var(--rbw-gray-dark)" }}>Total Sales</span>
                <span className="text-2xl font-bold" style={{ color: "var(--rbw-navy)" }}>
                  ${Number(z.total_sales).toFixed(2)}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between">
                  <span style={{ color: "var(--rbw-gray-dark)" }}>Transactions</span>
                  <span className="font-bold" style={{ color: "var(--rbw-navy)" }}>{z.transaction_count}</span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: "var(--rbw-gray-dark)" }}>Voids</span>
                  <span className="font-bold" style={{ color: Number(z.void_count) > 0 ? "var(--rbw-danger)" : "var(--rbw-navy)" }}>{z.void_count}</span>
                </div>
              </div>
              {Number(z.tips_total || 0) > 0 && (
                // Tips came in on cards and are not in Total Sales or the drawer.
                <div className="flex justify-between text-sm mt-2">
                  <span style={{ color: "var(--rbw-gray-dark)" }}>Card tips ({z.tip_count}), not in sales</span>
                  <span className="font-bold" style={{ color: "var(--rbw-success)" }}>${Number(z.tips_total).toFixed(2)}</span>
                </div>
              )}
            </div>

            {/* Payment breakdown */}
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-lg p-3 text-center" style={{ background: "#F6BE0015" }}>
                <div className="text-xs font-bold" style={{ color: "var(--rbw-gold-dark)" }}>CASH</div>
                <div className="text-lg font-bold" style={{ color: "var(--rbw-navy)" }}>${Number(z.cash_sales).toFixed(0)}</div>
              </div>
              <div className="rounded-lg p-3 text-center" style={{ background: "#8BB8E815" }}>
                <div className="text-xs font-bold" style={{ color: "var(--rbw-blue-dark)" }}>CREDIT</div>
                <div className="text-lg font-bold" style={{ color: "var(--rbw-navy)" }}>${Number(z.credit_sales).toFixed(0)}</div>
              </div>
              <div className="rounded-lg p-3 text-center" style={{ background: "#2D8A4E15" }}>
                <div className="text-xs font-bold" style={{ color: "var(--rbw-success)" }}>DEBIT</div>
                <div className="text-lg font-bold" style={{ color: "var(--rbw-navy)" }}>${Number(z.debit_sales).toFixed(0)}</div>
              </div>
            </div>

            {/* Drawer recap */}
            <div style={{ borderTop: "1px solid var(--rbw-border)", paddingTop: "12px" }}>
              <div className="flex justify-between text-sm mb-1">
                <span style={{ color: "var(--rbw-gray-dark)" }}>Drawer Counted</span>
                <span className="font-bold">${grandTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm mb-1">
                <span style={{ color: "var(--rbw-gray-dark)" }}>Expected</span>
                <span className="font-bold">${expectedInDrawer.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm mb-1">
                <span style={{ color: "var(--rbw-gray-dark)" }}>Over/Short</span>
                <span className="font-bold" style={{
                  color: overShort === 0 ? "var(--rbw-success)" : overShort > 0 ? "var(--rbw-blue-dark)" : "var(--rbw-danger)"
                }}>
                  {overShort >= 0 ? "+" : ""}${overShort.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between text-sm font-bold mt-2 pt-2" style={{ borderTop: "1px dashed var(--rbw-border)" }}>
                <span style={{ color: "var(--rbw-gold-dark)" }}>Pull for Deposit</span>
                <span className="text-lg" style={{ color: "var(--rbw-navy)" }}>${pullForDeposit.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="px-6 py-4" style={{ borderTop: "2px solid var(--rbw-border)", background: "var(--rbw-bg)" }}>
            <button
              onClick={() => onComplete(grandTotal)}
              className="w-full py-4 rounded-xl font-bold text-lg active:scale-[0.98]"
              style={{ background: "var(--rbw-navy)", color: "var(--rbw-gold)" }}
            >
              Done — Close Register
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Closeout summary screen
  if (step === "closeout") {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: "rgba(51,63,72,0.85)" }}>
        <div
          className="w-full max-w-md rounded-2xl overflow-hidden"
          style={{ background: "var(--rbw-bg-card)", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}
        >
          <div className="px-6 py-4" style={{ background: "var(--rbw-navy)" }}>
            <h2 className="text-lg font-bold text-white">Drawer Closeout</h2>
            <p className="text-xs" style={{ color: "var(--rbw-gray)" }}>Review and confirm</p>
          </div>

          <div className="px-6 py-5 space-y-3">
            {/* Count summary */}
            <div className="flex justify-between items-center py-2">
              <span className="text-sm" style={{ color: "var(--rbw-gray-dark)" }}>Drawer Counted</span>
              <span className="text-xl font-bold" style={{ color: "var(--rbw-navy)" }}>${grandTotal.toFixed(2)}</span>
            </div>

            <div style={{ borderTop: "1px solid var(--rbw-border)" }} />

            {/* Reconciliation */}
            <div className="flex justify-between items-center py-1">
              <span className="text-sm" style={{ color: "var(--rbw-gray-dark)" }}>Opening Balance</span>
              <span className="text-sm font-medium" style={{ color: "var(--rbw-navy)" }}>${openingBalance.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center py-1">
              <span className="text-sm" style={{ color: "var(--rbw-gray-dark)" }}>+ Cash Sales</span>
              <span className="text-sm font-medium" style={{ color: "var(--rbw-success)" }}>+${cashSalesTotal.toFixed(2)}</span>
            </div>
            {cashRefundsTotal > 0 && (
              <div className="flex justify-between items-center py-1">
                <span className="text-sm" style={{ color: "var(--rbw-gray-dark)" }}>- Cash Refunds</span>
                <span className="text-sm font-medium" style={{ color: "var(--rbw-danger)" }}>-${cashRefundsTotal.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between items-center py-1">
              <span className="text-sm font-medium" style={{ color: "var(--rbw-navy)" }}>Expected in Drawer</span>
              <span className="text-sm font-bold" style={{ color: "var(--rbw-navy)" }}>${expectedInDrawer.toFixed(2)}</span>
            </div>

            {/* Over/Short */}
            <div
              className="flex justify-between items-center py-3 px-4 rounded-xl"
              style={{
                background: overShort === 0 ? "#2D8A4E10" : overShort > 0 ? "#8BB8E810" : "#C5303010",
                border: `1px solid ${overShort === 0 ? "#2D8A4E30" : overShort > 0 ? "#8BB8E830" : "#C5303030"}`,
              }}
            >
              <span className="text-sm font-semibold" style={{ color: "var(--rbw-navy)" }}>
                {overShort === 0 ? "Balanced" : overShort > 0 ? "Over" : "Short"}
              </span>
              <span
                className="text-lg font-bold"
                style={{
                  color: overShort === 0 ? "var(--rbw-success)" : overShort > 0 ? "var(--rbw-blue-dark)" : "var(--rbw-danger)",
                }}
              >
                {overShort >= 0 ? "+" : ""}${overShort.toFixed(2)}
              </span>
            </div>

            <div style={{ borderTop: "1px solid var(--rbw-border)" }} />

            {/* Deposit instructions */}
            <div
              className="py-4 px-4 rounded-xl"
              style={{ background: "#F6BE0010", border: "1px solid #F6BE0030" }}
            >
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-semibold" style={{ color: "var(--rbw-gold-dark)" }}>
                  Leave in Drawer
                </span>
                <span className="text-lg font-bold" style={{ color: "var(--rbw-navy)" }}>
                  ${leaveInDrawer.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-bold" style={{ color: "var(--rbw-gold-dark)" }}>
                  PULL FOR DEPOSIT
                </span>
                <span className="text-2xl font-bold" style={{ color: "var(--rbw-navy)" }}>
                  ${pullForDeposit.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 flex gap-3" style={{ borderTop: "2px solid var(--rbw-border)", background: "var(--rbw-bg)" }}>
            <button
              onClick={() => setStep("count")}
              className="flex-1 py-3 rounded-xl font-semibold"
              style={{ background: "var(--rbw-gray-light)", color: "var(--rbw-navy)" }}
            >
              Back to Count
            </button>
            <button
              onClick={handleSubmit}
              className="flex-1 py-3 rounded-xl font-bold active:scale-[0.98]"
              style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}
            >
              Confirm Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Denomination counting screen
  return (
    <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: "rgba(51,63,72,0.85)" }}>
      <div
        className="w-full max-w-lg rounded-2xl overflow-hidden"
        style={{ background: "var(--rbw-bg-card)", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}
      >
        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between" style={{ background: "var(--rbw-navy)" }}>
          <div>
            <h2 className="text-lg font-bold text-white">
              {mode === "open" ? "Count Opening Drawer" : "Count Closing Drawer"}
            </h2>
            <p className="text-xs" style={{ color: "var(--rbw-gray)" }}>
              Enter the quantity of each denomination
            </p>
          </div>
          <button onClick={onCancel} className="text-2xl leading-none" style={{ color: "var(--rbw-gray)" }}>
            &times;
          </button>
        </div>

        <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
          {/* Coins */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: "var(--rbw-gray-dark)" }}>Coins</h3>
              <span className="text-sm font-bold" style={{ color: "var(--rbw-navy)" }}>${coinTotal.toFixed(2)}</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {DENOMINATIONS.slice(0, 6).map((denom, i) => (
                <div key={i} className="rounded-xl p-3" style={{ background: "var(--rbw-bg)", border: "1px solid var(--rbw-border)" }}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-semibold" style={{ color: "var(--rbw-navy)" }}>{denom.label}</span>
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: "var(--rbw-gray-light)", color: "var(--rbw-gray-dark)" }}>{denom.icon}</span>
                  </div>
                  <input
                    type="number"
                    value={counts[i]}
                    onChange={(e) => updateCount(i, e.target.value)}
                    placeholder="0"
                    min="0"
                    className="w-full px-2 py-1.5 rounded-lg text-center text-sm font-medium outline-none"
                    style={{ border: "2px solid var(--rbw-border)", background: "var(--rbw-bg-card)", color: "var(--rbw-navy)" }}
                    onFocus={(e) => (e.target.style.borderColor = "var(--rbw-blue)")}
                    onBlur={(e) => (e.target.style.borderColor = "var(--rbw-border)")}
                  />
                  {getSubtotal(i) > 0 && (
                    <div className="text-right text-xs font-medium mt-1" style={{ color: "var(--rbw-gold-dark)" }}>${getSubtotal(i).toFixed(2)}</div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Bills */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: "var(--rbw-gray-dark)" }}>Bills</h3>
              <span className="text-sm font-bold" style={{ color: "var(--rbw-navy)" }}>${billTotal.toFixed(2)}</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {DENOMINATIONS.slice(6).map((denom, i) => {
                const idx = i + 6;
                return (
                  <div key={idx} className="rounded-xl p-3" style={{ background: "var(--rbw-bg)", border: "1px solid var(--rbw-border)" }}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-semibold" style={{ color: "var(--rbw-navy)" }}>{denom.label}</span>
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: "#2D8A4E20", color: "var(--rbw-success)" }}>{denom.icon}</span>
                    </div>
                    <input
                      type="number"
                      value={counts[idx]}
                      onChange={(e) => updateCount(idx, e.target.value)}
                      placeholder="0"
                      min="0"
                      className="w-full px-2 py-1.5 rounded-lg text-center text-sm font-medium outline-none"
                      style={{ border: "2px solid var(--rbw-border)", background: "var(--rbw-bg-card)", color: "var(--rbw-navy)" }}
                      onFocus={(e) => (e.target.style.borderColor = "var(--rbw-blue)")}
                      onBlur={(e) => (e.target.style.borderColor = "var(--rbw-border)")}
                    />
                    {getSubtotal(idx) > 0 && (
                      <div className="text-right text-xs font-medium mt-1" style={{ color: "var(--rbw-gold-dark)" }}>${getSubtotal(idx).toFixed(2)}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4" style={{ borderTop: "2px solid var(--rbw-border)", background: "var(--rbw-bg)" }}>
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-semibold" style={{ color: "var(--rbw-gray-dark)" }}>Drawer Total</span>
            <span className="text-3xl font-bold" style={{ color: "var(--rbw-navy)" }}>${grandTotal.toFixed(2)}</span>
          </div>
          <div className="flex gap-3">
            <button onClick={onCancel} className="flex-1 py-3 rounded-xl font-semibold" style={{ background: "var(--rbw-gray-light)", color: "var(--rbw-navy)" }}>
              Cancel
            </button>
            <button onClick={handleCountDone} className="flex-1 py-3 rounded-xl font-bold active:scale-[0.98]" style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}>
              {mode === "open" ? "Open Drawer" : "Next: Closeout"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
